import { useEffect, useState } from 'react'
import { NextRouter } from 'next/router'

import Accounts, { Account } from '@ecm/data/Account'
import { isMarketingError } from '@ecm/data/Api'
import Billings, {
  Billing,
  hasAddressFieldsChanged,
  hasCompletedAddress,
  removeAddressIfNotComplete,
} from '@ecm/data/Billing'
import Carts, { Cart } from '@ecm/data/Cart'
import { getOrElse } from '@ecm/data/Maybe'
import { emptyOrderEstimate, OrderEstimate } from '@ecm/data/Order'
import { createCustomError, dispatchValidationError } from '@ecm/data/Validation'
import { ClientEff, runClientEff } from '@ecm/effect'

export type AccountUpdater = (account: Account) => void

export type BillingUpdater = (billing: Partial<Billing>, dontUpdateOrderSummary?: boolean) => void

export type CartUpdater = (cart: Partial<Cart>, dontUpdateOrderSummary?: boolean) => void

export const useOrderInfo = (
  router: NextRouter,
  validateCart?: boolean, // note that these three validateX parameters are
  validateAccount?: boolean, // defined in the order that they happen in the
  validateBilling?: boolean, // checkout process.
  calculateOrderEstimate?: boolean
): {
  account: Account
  billing: Billing
  cart: Cart
  orderEstimate: OrderEstimate
  updateBilling: BillingUpdater
  updateAccount: AccountUpdater
  updateCart: CartUpdater
} => {
  const [cart, setCart] = useState<Cart>(Carts.empty())
  const [orderEstimate, setOrderEstimate] = useState<OrderEstimate>(emptyOrderEstimate())
  const [billing, setBilling] = useState<Billing>(Billings.empty())
  const [account, setAccount] = useState<Account>(Accounts.empty())

  const updateAccount: AccountUpdater = (newAccount: Account) => {
    setAccount({ ...newAccount })
    runClientEff(eff => {
      eff.updateStorageItem('account', newAccount)
    })
  }

  const updateCart: CartUpdater = (
    pCart: Partial<Cart>,
    dontUpdateOrderSummary?: boolean
  ): void => {
    const newCart = { ...cart, ...pCart }
    setCart(newCart)
    runClientEff(async eff => {
      eff.updateStorageItem('cart', newCart)
      if (!dontUpdateOrderSummary) {
        await refreshOrderEstimate(eff, newCart, billing)
      }
    })
  }

  const updateBilling: BillingUpdater = (pBilling: Partial<Billing>): void => {
    setBilling(memoryBilling => {
      const newBilling = { ...memoryBilling, ...pBilling }

      runClientEff(async eff => {
        eff.updateStorageItem('billing', newBilling)

        //only update OrderEstimate on billing change if the address has changed and the address is valid
        if (
          cart.plan.handle &&
          hasAddressFieldsChanged(billing, pBilling) &&
          hasCompletedAddress(newBilling)
        ) {
          await refreshOrderEstimate(eff, cart, newBilling)
        }
      })

      return newBilling
    })
  }

  const refreshOrderEstimate = async (eff: ClientEff, cart: Cart, billing: Billing) => {
    const orderEstimateResponse = await eff.getOrderEstimate(
      cart,
      removeAddressIfNotComplete(billing)
    )
    if (!isMarketingError(orderEstimateResponse)) {
      eff.setStorageItem<number>(
        'priceEstimate',
        orderEstimateResponse.next_estimate.subtotal_in_cents / 100
      )
      eff.setStorageItem<OrderEstimate>('orderEstimate', orderEstimateResponse)
      setOrderEstimate(orderEstimateResponse)
    }
  }

  useEffect(() => {
    runClientEff(async eff => {
      try {
        // Because the order of the checkout flow is:
        // Cart -> Account -> Billing
        // we want to validate those objects in the same order.
        const memoryCart = getOrElse(eff.getStorageItem<Cart>('cart'), Carts.empty())
        if (validateCart && !Carts.isValidCart(memoryCart)) {
          eff.setStorageItem<boolean>('notValidCart', true)
          throw createCustomError('Addresses were not correct for selected plan', 'CartValidation')
        }
        const constructedCart = Carts.constructor(memoryCart)
        setCart(constructedCart)

        const memoryAccount = getOrElse(eff.getStorageItem<Account>('account'), Accounts.empty())
        if (validateAccount && !Accounts.isValidAccount(memoryAccount)) {
          throw createCustomError('Account info was not valid', 'AccountValidation')
        }
        setAccount(Accounts.account(memoryAccount))

        const memoryBilling = getOrElse(eff.getStorageItem<Billing>('billing'), Billings.empty())

        if (validateBilling && !Billings.isValidBilling(memoryBilling)) {
          throw createCustomError('Billing info was not valid', 'BillingValidation')
        }
        const constructedBilling = Billings.billing(memoryBilling)
        updateBilling(constructedBilling)

        if (calculateOrderEstimate) {
          await refreshOrderEstimate(eff, constructedCart, constructedBilling)
        }
      } catch (e) {
        dispatchValidationError(router, e as Error)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    account,
    billing,
    cart,
    orderEstimate,
    updateAccount,
    updateBilling,
    updateCart,
  }
}

export default useOrderInfo
